import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listPublishedPosts } from '../api/posts';
import FormattedDate from '../components/FormattedDate';
import Pagination from '../components/Pagination';
import BookPage from '../components/common/BookPage';
import { AiOutlineArrowRight, AiOutlineCalendar, AiOutlineFileText } from 'react-icons/ai';

const PAGE_SIZE = 10;

const BlogListPage: React.FC = () => {
  const [offset, setOffset] = useState(0);
  const postsQuery = useQuery({
    queryKey: ['posts', 'published', offset],
    queryFn: () => listPublishedPosts(PAGE_SIZE, offset),
    placeholderData: (previousData) => previousData,
  });

  const posts = postsQuery.data ?? [];
  const error = postsQuery.error instanceof Error ? postsQuery.error.message : '';
  const hasMore = posts.length === PAGE_SIZE;

  return (
    <BookPage
      eyebrow="Thoughts"
      title="I think a lot."
      intro="Sometimes I share."
    >
      <div>
        {error && (
          <div className="mb-6 rounded-[0.8rem] border border-red-200 bg-red-50/80 p-4 text-center text-sm text-red-700">
            {error}
          </div>
        )}

        {postsQuery.isLoading ? (
          <div className="py-16 text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-2 border-[color:var(--wf-brass)]/25 border-t-[color:var(--wf-plum)]"></div>
            <p className="mt-4 text-[color:var(--wf-ink-soft)]">Loading thoughts...</p>
          </div>
        ) : !posts || posts.length === 0 ? (
          <div className="py-16 text-center">
            <AiOutlineFileText className="mx-auto mb-4 text-6xl text-[color:var(--wf-brass-soft)]" />
            <h3 className="font-serif text-2xl text-[color:var(--wf-ink)]">Nothing here yet.</h3>
            <p className="mt-2 text-[color:var(--wf-ink-soft)]">No published thoughts are available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="border-y border-[color:var(--wf-border)]">
              {posts.map((post, index) => (
                <article
                  key={post.id}
                  className={`py-6 transition-colors duration-300 hover:bg-[rgba(255,255,255,0.22)] sm:py-7 ${index !== posts.length - 1 ? 'border-b border-[color:var(--wf-border)]/80' : ''}`}
                >
                  <Link to={`/blog/${post.id}`} className="group block">
                    <div className="grid gap-3 md:grid-cols-[9rem_minmax(0,1fr)_auto] md:items-start md:gap-6">
                      <div className="flex items-center text-xs uppercase tracking-[0.18em] text-[color:var(--wf-ink-soft)]">
                        <AiOutlineCalendar className="mr-2 text-sm text-[color:var(--wf-brass)]" />
                        <FormattedDate date={post.published_at} />
                      </div>

                      <div>
                        <h2 className="font-serif text-2xl text-[color:var(--wf-ink)] transition-colors duration-300 group-hover:text-[color:var(--wf-plum)] sm:text-[2rem]">
                          {post.title}
                        </h2>
                        <p className="mt-3 max-w-3xl leading-8 text-[color:var(--wf-ink-soft)]">{post.excerpt}</p>
                      </div>

                      <div className="inline-flex items-center text-sm uppercase tracking-[0.18em] text-[color:var(--wf-plum)] transition-colors duration-300 group-hover:text-[color:var(--wf-ink)] md:justify-self-end">
                        Read
                        <AiOutlineArrowRight className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>

            <div className="mt-12">
              <Pagination offset={offset} pageSize={PAGE_SIZE} hasMore={hasMore} onPageChange={setOffset} />
            </div>
          </>
        )}
      </div>
    </BookPage>
  );
};

export default BlogListPage;
